import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Loader2, CheckCircle2, ShieldCheck, ShieldAlert, FileText, Car, Calendar, MapPin, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "../lib/api";

const money = (v) => `R ${Number(v || 0).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`;

const StatPill = ({ label, value, tone = "default" }) => (
  <div className={`px-4 py-3 rounded-md border ${tone === "warn" ? "bg-[#dc2626]/5 border-[#dc2626]/30" : "bg-white border-[#cfd7db]"}`}>
    <div className="text-xs uppercase tracking-widest font-display text-[#3f4b52]">{label}</div>
    <div className="font-display font-bold text-lg text-[#111619] mt-0.5">{value}</div>
  </div>
);

const ClientPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setData(await publicApi.getPortal(token)); setNotFound(false); }
    catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(e?.response?.data?.detail || "Could not load");
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const accept = async () => {
    if (!window.confirm("Accept this quotation? We will contact you to confirm the fitment.")) return;
    setAccepting(true);
    try {
      const res = await publicApi.acceptQuote(token);
      toast.success("Thank you — your acceptance has been recorded.");
      setData((d) => ({ ...d, job: { ...d.job, quote_accepted_at: res.accepted_at } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept");
    } finally { setAccepting(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0f1416] flex items-center justify-center text-white" data-testid="portal-loading">
      <Loader2 className="animate-spin mr-2" size={16} /> Loading your quotation…
    </div>
  );

  if (notFound || !data) return (
    <div className="min-h-screen bg-[#0f1416] flex items-center justify-center p-6" data-testid="portal-invalid">
      <div className="bg-white rounded-md p-8 max-w-md text-center">
        <ShieldAlert className="mx-auto text-[#dc2626]" size={36} />
        <h1 className="mt-3 font-display uppercase tracking-widest text-[#111619]">Link no longer valid</h1>
        <p className="text-sm text-[#3f4b52] mt-2">
          This secure link has expired or been revoked. Please contact Diesel &amp; Dust to request a new one.
        </p>
      </div>
    </div>
  );

  const { company, job, customer, totals, documents, scopes, quote_request, resource_kind } = data;

  // ==== Quote request (pre-conversion acknowledgement) ==================
  if (resource_kind === "quote_request" && quote_request) {
    return (
      <div className="min-h-screen bg-[#f5f6f6]" data-testid="portal-page">
        <header className="bg-[#0f1416] text-white">
          <div className="max-w-3xl mx-auto px-6 py-8 flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="font-display font-black uppercase text-2xl sm:text-3xl leading-none">Diesel &amp; Dust</div>
              <div className="text-[#f28a16] font-display uppercase tracking-widest text-xs mt-1">{company.trading_name}</div>
            </div>
            <div className="text-right text-xs text-[#cfd7db]">
              <div className="inline-flex items-center gap-1"><Mail size={12} /> {company.email}</div>
              <div className="mt-0.5 inline-flex items-center gap-1"><Phone size={12} /> {company.cell}</div>
              {company.website && <div className="mt-0.5">{company.website}</div>}
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto p-6 space-y-6">
          <div className="bg-white border border-[#cfd7db] rounded-md p-6 md:p-8" data-testid="portal-request-ack">
            <div className="text-xs font-display uppercase tracking-widest text-[#f28a16]">Quote Request Received</div>
            <h1 className="mt-1 font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">
              Thanks, {quote_request.name || "there"}!
            </h1>
            <p className="text-[#3f4b52] mt-2">
              We've received your quote request and one of our team will be in touch shortly with a formal quotation and price.
              Please check the details below and let us know if anything needs to change.
            </p>

            {quote_request.status === "converted" && quote_request.converted_job_id && (
              <div className="mt-4 bg-[#10b981]/10 border border-[#10b981]/30 text-[#065f46] rounded-md p-3 text-sm">
                <CheckCircle2 size={14} className="inline mr-1" />
                Your quotation is being prepared. You'll receive an updated link with your formal quote and pricing.
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#cfd7db] rounded-md p-5">
              <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] flex items-center gap-2"><Car size={14} /> Vehicle</div>
              <div className="mt-2 text-[#111619] font-semibold">
                {[quote_request.vehicle_make, quote_request.vehicle_model, quote_request.vehicle_year].filter(Boolean).join(" ") || "—"}
              </div>
              <div className="text-sm text-[#3f4b52]">Registration: {quote_request.registration_number || "—"}</div>
              {quote_request.insurance && (
                <div className="text-sm text-[#3f4b52] mt-1">
                  Insurance: {quote_request.insurance}
                  {quote_request.policy_number && <> · Policy {quote_request.policy_number}</>}
                </div>
              )}
            </div>
            <div className="bg-white border border-[#cfd7db] rounded-md p-5">
              <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] flex items-center gap-2"><MapPin size={14} /> Location</div>
              <div className="mt-2 text-[#111619] font-semibold">{quote_request.location || "—"}</div>
              <div className="text-xs text-[#3f4b52] mt-2">
                Submitted {(quote_request.created_at || "").slice(0, 10)}
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#cfd7db] rounded-md p-5">
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Damage Description</div>
            <div className="mt-2 text-[#111619]">{quote_request.damage_description || "—"}</div>
          </div>

          <div className="bg-[#111619] text-white rounded-md p-6">
            <div className="text-xs font-display uppercase tracking-widest text-[#f28a16]">Next steps</div>
            <div className="mt-2 text-sm">
              We'll call or WhatsApp you on the number you provided. If you'd like to send us any extra info, please reply to the email that brought you here — we'll add it to your file.
            </div>
          </div>

          <footer className="text-center text-xs text-[#3f4b52] py-6">
            <ShieldCheck size={12} className="inline mr-1" /> Secure private link · expires {(data.expires_at || "").slice(0, 10)}
            <div className="mt-1">© {new Date().getFullYear()} Diesel &amp; Dust Group</div>
          </footer>
        </main>
      </div>
    );
  }

  // ==== Existing job-based view (quote / invoice / claim form) =========
  const canAcceptQuote = scopes.includes("accept_quote") && !job.quote_accepted_at && !job.voided;
  const canDownload = scopes.includes("download_docs");

  return (
    <div className="min-h-screen bg-[#f5f6f6]" data-testid="portal-page">
      {/* Header */}
      <header className="bg-[#0f1416] text-white">
        <div className="max-w-5xl mx-auto px-6 py-8 flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="font-display font-black uppercase text-2xl sm:text-3xl leading-none">Diesel &amp; Dust</div>
            <div className="text-[#f28a16] font-display uppercase tracking-widest text-xs mt-1">{company.trading_name}</div>
          </div>
          <div className="text-right text-xs text-[#cfd7db]">
            <div className="inline-flex items-center gap-1"><Mail size={12} /> {company.email}</div>
            <div className="mt-0.5 inline-flex items-center gap-1"><Phone size={12} /> {company.cell}</div>
            {company.website && <div className="mt-0.5">{company.website}</div>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6 space-y-6">
        {/* Hero */}
        <div className="bg-white border border-[#cfd7db] rounded-md p-6 md:p-8">
          <div className="text-xs font-display uppercase tracking-widest text-[#f28a16]">
            {job.tax_invoice_number ? "Your Tax Invoice" : "Your Quotation"}
          </div>
          <h1 className="mt-1 font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">
            {job.tax_invoice_number ? `Invoice ${job.tax_invoice_number}` : (job.quotation_number ? `Quote ${job.quotation_number}` : `Job #${job.job_number}`)}
          </h1>
          <p className="text-[#3f4b52] mt-1">Hi {job.client_name || (customer && customer.name) || "there"} — everything you need is on this page.</p>

          {job.voided && (
            <div className="mt-4 bg-[#dc2626]/10 border border-[#dc2626]/30 text-[#7f1d1d] rounded-md p-3 text-sm">
              This job has been voided. Please contact us for any queries.
            </div>
          )}

          {job.quote_accepted_at && !job.voided && (
            <div className="mt-4 bg-[#10b981]/10 border border-[#10b981]/30 text-[#065f46] rounded-md p-3 text-sm inline-flex items-center gap-2" data-testid="portal-accepted-banner">
              <CheckCircle2 size={16} /> Thank you — you accepted this quote on {(job.quote_accepted_at || "").slice(0, 10)}.
            </div>
          )}
        </div>

        {/* Job details */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-[#cfd7db] rounded-md p-5">
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] flex items-center gap-2"><Car size={14} /> Vehicle</div>
            <div className="mt-2 text-[#111619] font-semibold">{job.vehicle_make_model || "—"}</div>
            <div className="text-sm text-[#3f4b52]">Registration: {job.registration || "—"}</div>
          </div>
          <div className="bg-white border border-[#cfd7db] rounded-md p-5">
            <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] flex items-center gap-2"><Calendar size={14} /> Fitment</div>
            <div className="mt-2 text-[#111619] font-semibold">{job.fitment_date || "To be scheduled"}</div>
            <div className="text-sm text-[#3f4b52] flex items-center gap-1"><MapPin size={12} /> {job.fitment_address || "—"}</div>
          </div>
        </div>

        {/* Totals */}
        <div className="bg-white border border-[#cfd7db] rounded-md p-5">
          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52]">Amounts</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatPill label="Subtotal (ex VAT)" value={money(totals.subtotal)} />
            <StatPill label="VAT" value={money(totals.vat)} />
            <StatPill label="Total (inc VAT)" value={money(totals.gross)} />
            <StatPill
              label={totals.outstanding > 0 ? "Balance due" : "Paid in full"}
              value={money(totals.outstanding)}
              tone={totals.outstanding > 0 ? "warn" : "default"}
            />
          </div>
          {job.insurance_company && (
            <p className="mt-3 text-xs text-[#3f4b52]">
              Insurance claim through <b>{job.insurance_company}</b>
              {job.policy_number && ` · Policy ${job.policy_number}`}
              {job.claim_number && ` · Claim ${job.claim_number}`}. Your excess payable is <b>{money(totals.excess)}</b>.
            </p>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white border border-[#cfd7db] rounded-md p-5">
          <div className="text-xs font-display uppercase tracking-widest text-[#3f4b52] mb-3">Documents</div>
          {documents.length === 0 ? (
            <div className="text-sm text-[#3f4b52]">No documents available yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {documents.map((d) => (
                canDownload ? (
                  <a
                    key={d.kind}
                    href={publicApi.portalDocUrl(token, d.kind)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`portal-doc-${d.kind}`}
                    className="border border-[#cfd7db] rounded-md p-4 hover:border-[#f28a16] hover:bg-[#f28a16]/5 transition inline-flex items-center gap-3"
                  >
                    <FileText size={22} className="text-[#f28a16]" />
                    <div>
                      <div className="font-semibold text-[#111619]">{d.label}</div>
                      <div className="text-xs text-[#3f4b52]">{d.filename}</div>
                    </div>
                  </a>
                ) : (
                  <div key={d.kind} className="border border-[#cfd7db] rounded-md p-4 opacity-60 inline-flex items-center gap-3">
                    <FileText size={22} className="text-[#3f4b52]" />
                    <div>
                      <div className="font-semibold text-[#111619]">{d.label}</div>
                      <div className="text-xs text-[#3f4b52]">Download not available on this link</div>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>

        {/* Accept button */}
        {canAcceptQuote && (
          <div className="bg-[#111619] text-white rounded-md p-6 flex items-center justify-between flex-wrap gap-4" data-testid="portal-accept-block">
            <div>
              <div className="text-xs font-display uppercase tracking-widest text-[#f28a16]">Ready to go ahead?</div>
              <div className="mt-1 text-sm">Accepting this quote lets us book your fitment. You will only be charged after the work is complete.</div>
            </div>
            <button
              onClick={accept}
              disabled={accepting}
              data-testid="portal-accept-btn"
              className="bg-[#f28a16] text-white px-6 h-12 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
            >
              {accepting ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={16} />}
              Accept this quote
            </button>
          </div>
        )}

        <footer className="text-center text-xs text-[#3f4b52] py-6">
          <ShieldCheck size={12} className="inline mr-1" /> Secure private link for {job.client_name || "you"} · expires {(data.expires_at || "").slice(0, 10)}
          <div className="mt-1">© {new Date().getFullYear()} Diesel &amp; Dust Group</div>
        </footer>
      </main>
    </div>
  );
};

export default ClientPortalPage;
